/**
 * Lender Logo Mapping Configuration
 * Maps lender names (from database/Excel import) to their logo file paths
 *
 * IMPORTANT: Keys should match EXACTLY the lender names from the AFG Excel import
 */

// Exact lender name -> logo path mapping
const lenderLogoMap: Record<string, string> = {
  // AFG Products
  "AFG Home Loans - Align": "/lenders/AFG-Home-Loans-Align.png",
  "AFG Home Loans - Alpha": "/lenders/afg-home-loans-alpha.png",
  "AFG Home Loans - Bright": "/lenders/afg-home-loans-bright.jpeg",
  "AFG Home Loans - Link": "/lenders/AFG-Home-Loans-BG-Link.png",
  "AFG Home Loans - Options": "/lenders/AFG-Home-Loans-Options.png",
  "AFG Home Loans - Retro": "/lenders/AFG-Home-Loans-BG-Retro.png",

  // Major Banks
  "AMP Bank": "/lenders/amp-bank-logo.png",
  "AMP": "/lenders/amp-bank-logo.png",
  "ANZ": "/lenders/anz.png",
  "Commonwealth Bank": "/lenders/commbank.png",
  "CommBank": "/lenders/commbank.png",
  "CBA": "/lenders/commbank.png",
  "NAB": "/lenders/nab.png",
  "Westpac": "/lenders/westpac.png",
  "Citibank": "/lenders/citibank.png",

  // Second Tier Banks
  "Bank of Melbourne": "/lenders/bank-of-melbourne.jpg",
  "Bank of QLD": "/lenders/Bank_of_Queensland_Limited.png",
  "Bank of Queensland": "/lenders/Bank_of_Queensland_Limited.png",
  "BOQ": "/lenders/Bank_of_Queensland_Limited.png",
  "Bank SA": "/lenders/bank-sa.svg",
  "BankSA": "/lenders/bank-sa.svg",
  "Bankwest": "/lenders/bankwest.png",
  "HSBC": "/lenders/hsbc.png",
  "ING": "/lenders/ing.png",
  "ING Bank": "/lenders/ing.png",
  "Macquarie Bank": "/lenders/macquarie.png",
  "Macquarie": "/lenders/macquarie.png",
  "ME": "/lenders/mebank.png",
  "ME Bank": "/lenders/mebank.png",
  "St George Bank": "/lenders/st-george.webp",
  "St George": "/lenders/st-george.webp",
  "Suncorp Bank": "/lenders/suncorp.png",
  "Suncorp": "/lenders/suncorp.png",
  "UBank": "/lenders/nab.png", // UBank is owned by NAB

  // Regional & Mutual Banks
  "Australian Military Bank": "/lenders/australian-military-bank.jpg",
  "Auswide Bank": "/lenders/auswide-bank.svg",
  "Auswide": "/lenders/auswide-bank.svg",
  "Bank Australia": "/lenders/bank-australia.png",
  "Bank of China": "/lenders/bank-of-china-ltd.png",
  "Bank of Sydney": "/lenders/bank-of-sydney.png",
  "Beyond Bank": "/lenders/beyond-bank.webp",
  "Great Southern Bank": "/lenders/great-southern-bank.png",
  "Heritage Bank": "/lenders/heritage-bank.png",
  "Heritage": "/lenders/heritage-bank.png",
  "Hume Bank": "/lenders/hume-bank.png",
  "MyState": "/lenders/mystate-bank.png",
  "MyState Bank": "/lenders/mystate-bank.png",
  "Newcastle Permanent": "/lenders/newcastle-permanent.png",
  "P&N Bank": "/lenders/pn-bank.png",
  "P&N": "/lenders/pn-bank.png",
  "Queensland Country Bank": "/lenders/queensland-country-bank.png",
  "The Capricornian Bank": "/lenders/capricornian-bank.png",
  "Capricornian": "/lenders/capricornian-bank.png",

  // Credit Unions
  "BCU": "/lenders/bcu.png",
  "BCU Bank": "/lenders/bcu.png",
  "Credit Union SA": "/lenders/credit-union-sa.png",
  "Firefighters Mutual Bank": "/lenders/firefighters-mutual-bank.png",
  "GMCU": "/lenders/gmcu.png",
  "Health Professionals Bank": "/lenders/health-professionals-bank.png",
  "Peoples Choice Credit Union": "/lenders/pccu-logo.svg",
  "People's Choice": "/lenders/pccu-logo.svg",
  "QBANK": "/lenders/qbank.png",
  "Teachers Mutual Bank": "/lenders/firefighters-mutual-bank.png",
  "UniBank": "/lenders/firefighters-mutual-bank.png",

  // Non-Bank Lenders
  "ASCF": "/lenders/unnamed.png",
  "Firstmac": "/lenders/firstmac.png",
  "Granite Home Loans": "/lenders/granite-home-loans.webp",
  "Hejaz Financial Services": "/lenders/hejaz.png",
  "Hejaz": "/lenders/hejaz.png",
  "HomeStart Finance": "/lenders/homestart-finance.jpg",
  "HomeStart": "/lenders/homestart-finance.jpg",
  "Keystart": "/lenders/keystart-home-loans.jpg",
  "Keystart Home Loans": "/lenders/keystart-home-loans.jpg",
  "La Trobe Financial": "/lenders/la-trobe-financial.jpg",
  "La Trobe": "/lenders/la-trobe-financial.jpg",
  "Liberty Financial": "/lenders/liberty-financial.webp",
  "Liberty": "/lenders/liberty-financial.webp",
  "Pepper Money": "/lenders/pepper-money.png",
  "Pepper": "/lenders/pepper-money.png",
  "RedZed": "/lenders/redzed.jpg",
  "Resimac": "/lenders/resimac.png",
};

// Create lowercase version for case-insensitive lookup
const lowerCaseMap: Record<string, string> = {};
for (const [key, value] of Object.entries(lenderLogoMap)) {
  lowerCaseMap[key.toLowerCase()] = value;
}

/**
 * Get the logo URL for a lender by name
 * @param lenderName - The lender name from database
 * @returns The logo URL path or null if not found
 */
export function getLenderLogo(lenderName: string): string | null {
  if (!lenderName) return null;

  // Try exact match first
  if (lenderLogoMap[lenderName]) {
    return lenderLogoMap[lenderName];
  }

  // Try case-insensitive match
  const normalized = lenderName.toLowerCase().trim();
  if (lowerCaseMap[normalized]) {
    return lowerCaseMap[normalized];
  }

  // Try partial match - check if any key contains the name or vice versa
  for (const [key, logo] of Object.entries(lenderLogoMap)) {
    const keyLower = key.toLowerCase();
    if (normalized.includes(keyLower) || keyLower.includes(normalized)) {
      return logo;
    }
  }

  return null;
}

/**
 * Get all known lender names and their logos
 */
export function getAllLenderLogos(): { name: string; logo: string }[] {
  return Object.entries(lenderLogoMap).map(([name, logo]) => ({ name, logo }));
}

export { lenderLogoMap };
